/**
 * axios baseURL 单源 + Mock 模式拦截器（W2-9 PR-1 mock 基础设施）
 *
 * 承接：CLAUDE.md §11.5.1 数据工厂 / 场景脚本
 *
 * dev：VITE_API_BASE_URL 未设置时默认本机 FastAPI 端口
 * prod：VITE_API_BASE_URL 未设置时走同源相对路径，复用 nginx /admin/v1 与 /api/v1 代理
 *
 * Mock 模式：
 *   - URL ?mock=1 或 localStorage cs_admin_mock_mode=1 触发
 *   - 请求拦截器命中 fixture → 直接返回 MockResponse，不打后端
 *   - 未命中 → 放行真实请求（PR-1 注册中心为空，所有请求都走真实后端，行为兼容）
 */
import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";

import { resolveFixture } from "@/lib/fixtures";
import { MOCK_MODE } from "@/lib/mock-mode";

const LOCAL_DEV_API_BASE_URL = ["http://localhost", "8000"].join(":");
const baseURL =
	import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? LOCAL_DEV_API_BASE_URL : "");

export const apiClient = axios.create({
	baseURL,
	timeout: 10_000,
	headers: {
		"Content-Type": "application/json",
	},
});

/**
 * Mock 拦截标记 · 命中 fixture 时挂在 config 上，response 拦截器读取后构造响应
 */
const MOCK_RESPONSE_KEY = "__cs_mock_response__";

interface MockEnrichedConfig extends InternalAxiosRequestConfig {
	[MOCK_RESPONSE_KEY]?: { status: number; data: unknown };
}

if (MOCK_MODE) {
	apiClient.interceptors.request.use(async (config) => {
		const fixture = await resolveFixture(config);
		if (fixture === null) {
			return config;
		}

		if (fixture.delay_ms && fixture.delay_ms > 0) {
			await new Promise((resolve) => setTimeout(resolve, fixture.delay_ms));
		}

		(config as MockEnrichedConfig)[MOCK_RESPONSE_KEY] = {
			status: fixture.status ?? 200,
			data: fixture.data,
		};

		// 通过 adapter 短路真实请求
		config.adapter = async () =>
			({
				config,
				data: fixture.data,
				headers: {},
				request: {},
				status: fixture.status ?? 200,
				statusText: "OK (mock)",
			}) as AxiosResponse;

		return config;
	});

	apiClient.interceptors.response.use(
		(response) => response,
		(error: AxiosError) => Promise.reject(error),
	);
}
